import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

const getDocumentUrl = (tenantId: number, relatedEntityId?: number) => {
  if (!tenantId) throw new Error("Tenant ID is required.");
  let url = `${API_BASE_URL}/document/?tenant_id=${tenantId}`;
  if (relatedEntityId) {
    url += `&related_entity_id=${relatedEntityId}`;
  }
  return url;
};

const addTenantToDocument = <T extends { tenant_id?: number | null }>(
  document: Partial<T>,
  tenantId: number
): Partial<T> => {
  if (!tenantId) {
    throw new Error("Tenant ID is required.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...document,
    tenant_id: tenantId,
    created_by: currentUserId,
  };
};

export interface Document {
  document_id?: number;
  title: string;
  content: string;
  document_type: string;
  assigned_user?: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  assigned_datetime?: string | null;
  created_by: {
    user_id: number;
    full_name: string;
    avatar_url: string;
  };
  created_datetime?: string;
  related_entity_name?: string | null;
  related_entity_id?: number | null;
  tenant_id: number;
}

export interface DocumentRequest {
  title: string;
  content: string;
  assigned_user_id?: number;
  document_type: string;
  tenant_id: number;
  related_entity_id: number;
  related_entity_name: string;
  created_by: number;
  attachment_urls?: File[];
}

export interface PaginatedDocumentResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Document[];
}

export interface DocumentAttachment {
  document_attachment_id: number;
  attachment_url: string;
  title: string;
  size_in_kb: number;
  created_datetime?: string | null;
  document_id: number;
  tenant_id: number;
  created_by: number;
}

class DocumentServiceClass {
  async getDocuments(
    tenantId: number,
    relatedEntityId: number,
    page: number = 1,
    limit: number = 20
  ): Promise<PaginatedDocumentResponse> {
    try {
      const url = getDocumentUrl(tenantId, relatedEntityId);
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        params: {
          page: page,
          limit: limit,
        },
      });

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch documents"
      );
    }
  }

  async getDocumentById(id: number, tenantId: number): Promise<Document> {
    try {
      const response = await axios.get(`${API_BASE_URL}/document/${id}/`, {
        headers: getAuthHeaders(),
        params: {
          tenant_id: tenantId,
        },
      });

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch document"
      );
    }
  }

  async createDocument(
    documentData: Partial<DocumentRequest>,
    tenantId: number,
    attachments?: File[]
  ): Promise<Document> {
    try {
      const documentWithTenant = addTenantToDocument(documentData, tenantId);

      const { attachment_urls, ...cleanDocumentData } = documentWithTenant;

      const response = await axios.post(
        `${API_BASE_URL}/document/`,
        cleanDocumentData,
        {
          headers: getAuthHeaders(),
        }
      );

      const createdDocument = response.data;

      if (
        attachments &&
        attachments.length > 0 &&
        createdDocument.document_id
      ) {
        await this.createDocumentAttachments(
          createdDocument.document_id,
          attachments
        );
      }

      return createdDocument;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create document"
      );
    }
  }

  async updateDocument(
    id: number,
    documentData: Partial<DocumentRequest>,
    tenantId: number,
    attachments?: File[]
  ): Promise<Document> {
    try {
      const authResponse = localStorage.getItem("authResponse");
      let currentUserId = null;

      if (authResponse) {
        try {
          const auth = JSON.parse(authResponse);
          currentUserId = auth.user_id;
        } catch (e) {
          console.warn("Could not parse auth response for user_id");
        }
      }

      const updatePayload = {
        title: documentData.title,
        content: documentData.content,
        document_type: documentData.document_type,
        assigned_user_id: documentData.assigned_user_id,
        tenant_id: tenantId,
        updated_by: currentUserId,
      };

      const response = await axios.patch(
        `${API_BASE_URL}/document/${id}/`,
        updatePayload,
        {
          headers: getAuthHeaders(),
          params: {
            tenant_id: tenantId,
          },
        }
      );

      const updatedDocument = response.data;

      if (attachments && attachments.length > 0) {
        await this.createDocumentAttachments(id, attachments);
      }

      return updatedDocument;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to update document"
      );
    }
  }

  async deleteDocument(id: number, tenantId: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/document/${id}/`, {
        headers: getAuthHeaders(),
        params: {
          tenant_id: tenantId,
        },
      });
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to delete document"
      );
    }
  }

  async getDocumentAttachments(
    documentId: number
  ): Promise<DocumentAttachment[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/document-attachment/?document_id=${documentId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      return response.data.results || response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch document attachments"
      );
    }
  }

  async deleteDocumentAttachment(attachmentId: number): Promise<void> {
    try {
      await axios.delete(
        `${API_BASE_URL}/document-attachment/${attachmentId}/`,
        {
          headers: getAuthHeaders(),
        }
      );
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to delete document attachment"
      );
    }
  }

  async createDocumentAttachment(
    documentId: number,
    file: File
  ): Promise<DocumentAttachment> {
    try {
      const formData = new FormData();
      formData.append("document_id", String(documentId));
      formData.append("attachement_url", file);

      const response = await axios.post(
        `${API_BASE_URL}/document-attachment/`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "multipart/form-data",
          },
          params: {
            document_id: documentId,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create document attachment"
      );
    }
  }

  async createDocumentAttachments(
    documentId: number,
    files: File[]
  ): Promise<DocumentAttachment[]> {
    try {
      const attachmentPromises = files.map((file) =>
        this.createDocumentAttachment(documentId, file)
      );

      const attachments = await Promise.all(attachmentPromises);
      return attachments;
    } catch (error: any) {
      handleAuthError(error);
      throw new Error(
        error.response?.data?.message || "Failed to create document attachments"
      );
    }
  }
}

export const DocumentService = new DocumentServiceClass();
